import {
    Check, Copy, ExternalLink, Eye,
    EyeOff, Key, Network,
    Plus, Settings, TestTube, Trash2, Webhook
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useMerchantAuth } from '../contexts/MerchantAuthContext';
import {
    API_BASE_URL,
    merchantAuthAPI
} from '../lib/api';
import { cabinetPath } from '../lib/domains';
import { resolveMerchantFiatCurrency } from '../lib/typicalV2';

const isValidIPv4 = (value: string): boolean => {
    const parts = value.split('.');
    return parts.length === 4 && parts.every((part) => {
        if (!/^\d{1,3}$/.test(part)) return false;
        if (part.length > 1 && part.startsWith('0')) return false;
        const number = Number(part);
        return number >= 0 && number <= 255;
    });
};

const isValidIPv6 = (value: string): boolean => {
    if (!value.includes(':') || value.includes('%')) return false;
    try {
        const parsed = new URL(`http://[${value}]/`);
        return parsed.hostname.startsWith('[') && parsed.hostname.endsWith(']');
    } catch {
        return false;
    }
};

const isValidIPOrCIDR = (rawValue: string): boolean => {
    const value = rawValue.trim();
    if (!value) return false;
    const slashIndex = value.indexOf('/');
    if (slashIndex !== value.lastIndexOf('/')) return false;

    const address = slashIndex >= 0 ? value.slice(0, slashIndex) : value;
    const prefixValue = slashIndex >= 0 ? value.slice(slashIndex + 1) : null;
    const isIPv4 = isValidIPv4(address);
    const isIPv6 = !isIPv4 && isValidIPv6(address);
    if (!isIPv4 && !isIPv6) return false;
    if (prefixValue === null) return true;
    if (!/^\d+$/.test(prefixValue)) return false;

    const prefix = Number(prefixValue);
    return prefix >= 0 && prefix <= (isIPv4 ? 32 : 128);
};

export const MerchantIntegrationPage: React.FC = () => {
    const navigate = useNavigate();
    const { user, refreshUser } = useMerchantAuth();
    const isTypicalIntegration = user?.integration_profile === 'typical_v2_2';
    const [error, setError] = useState<string | null>(null);
    const [showApiKey, setShowApiKey] = useState(false);
    const [showSandboxApiKey, setShowSandboxApiKey] = useState(false);
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [webhookUrl, setWebhookUrl] = useState('');
    const [webhookSaving, setWebhookSaving] = useState(false);
    const [showSecret, setShowSecret] = useState(false);
    const [showSandboxSecret, setShowSandboxSecret] = useState(false);
    const [ipAllowlist, setIPAllowlist] = useState<string[]>([]);
    const [ipAllowlistEnvironment, setIPAllowlistEnvironment] = useState<'live' | 'sandbox'>('live');
    const [ipAllowlistSaving, setIPAllowlistSaving] = useState(false);
    const [ipAllowlistError, setIPAllowlistError] = useState<string | null>(null);
    const [ipAllowlistSuccess, setIPAllowlistSuccess] = useState<string | null>(null);
    useEffect(() => {
        if (user) {
            setWebhookUrl(user.webhook_url || '');
            const selected = ipAllowlistEnvironment === 'live'
                ? user.live_api_ip_allowlist
                : user.sandbox_api_ip_allowlist;
            setIPAllowlist(selected?.length ? selected : (user.api_ip_allowlist || []));
        }
    }, [user, ipAllowlistEnvironment]);

    const copyToClipboard = async (text: string, fieldName: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(fieldName);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) {
            console.error('Ошибка копирования:', err);
        }
    };

    const handleSaveWebhook = async () => {
        setWebhookSaving(true);
        setError(null);
        try {
            await merchantAuthAPI.updateWebhook(webhookUrl.trim() || '');
            // Принудительно обновляем контекст мерчанта
            await refreshUser();
            console.log('Webhook сохранен и контекст обновлен');
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Не удалось сохранить webhook URL');
        } finally {
            setWebhookSaving(false);
        }
    };

    const handleSaveIPAllowlist = async () => {
        setIPAllowlistError(null);
        setIPAllowlistSuccess(null);

        const addresses = ipAllowlist.map((address) => address.trim());
        if (addresses.length > 50) {
            setIPAllowlistError('Можно добавить не более 50 адресов');
            return;
        }
        const invalidAddress = addresses.find((address) => !isValidIPOrCIDR(address));
        if (invalidAddress !== undefined) {
            setIPAllowlistError(
                invalidAddress
                    ? `Некорректный IP-адрес или CIDR: ${invalidAddress}`
                    : 'Заполните или удалите пустую строку'
            );
            return;
        }

        setIPAllowlistSaving(true);
        try {
            const response = await merchantAuthAPI.updateIPAllowlist(addresses, ipAllowlistEnvironment);
            setIPAllowlist(response.addresses);
            setIPAllowlistSuccess('Белый список сохранён');
            await refreshUser();
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            setIPAllowlistError(
                typeof detail === 'string' ? detail : 'Не удалось сохранить белый список'
            );
        } finally {
            setIPAllowlistSaving(false);
        }
    };

    if (!user) return null;
    const fiatCurrency = resolveMerchantFiatCurrency(user);
    const merchantMethods = user.requisite_types || (isTypicalIntegration ? [] : ['card', 'sbp']);
    return <div className="space-y-6">
        {error && <div role="alert" className="rounded-xl bg-red-50 p-4 text-red-700 dark:bg-red-950 dark:text-red-300">{error}</div>}
        {/* API Key Section */}
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Key className="h-5 w-5 text-green-600 dark:text-green-300" />
                    {isTypicalIntegration ? 'Ключи API' : 'API Ключ'}
                </CardTitle>
                <CardDescription>
                    {isTypicalIntegration
                        ? 'Ключ и секрет подписи для рабочего и тестового контуров'
                        : 'Используйте этот ключ для создания платежей через API'}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className={isTypicalIntegration
                    ? 'space-y-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950 p-4'
                    : 'space-y-2'}>
                    {isTypicalIntegration && (
                        <div>
                            <p className="font-semibold text-blue-900 dark:text-blue-200">Live</p>
                            <p className="text-xs text-blue-700 dark:text-blue-300">Рабочий контур</p>
                        </div>
                    )}
                    <Label htmlFor="api_key">
                        {isTypicalIntegration ? 'Live API ключ' : 'Ваш API ключ'}
                    </Label>
                    <div className="flex items-center gap-2">
                        <div className="relative min-w-0 flex-1">
                            <Input
                                id="api_key"
                                type={showApiKey ? 'text' : 'password'}
                                value={user.api_key}
                                readOnly
                                className="font-mono text-sm pr-10"
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-2 top-1/2 -translate-y-1/2 h-auto p-1"
                                aria-label={showApiKey ? 'Скрыть API-ключ' : 'Показать API-ключ'}
                                onClick={() => setShowApiKey(!showApiKey)}
                            >
                                {showApiKey ? (
                                    <EyeOff className="h-4 w-4" />
                                ) : (
                                    <Eye className="h-4 w-4" />
                                )}
                            </Button>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            aria-label="Скопировать API-ключ"
                            onClick={() => copyToClipboard(user.api_key, 'api_key')}
                        >
                            {copiedField === 'api_key' ? (
                                <Check className="h-4 w-4 text-green-600 dark:text-green-300" />
                            ) : (
                                <Copy className="h-4 w-4" />
                            )}
                        </Button>
                    </div>

                    {isTypicalIntegration && user.webhook_secret && (
                        <div className="space-y-2 pt-1">
                            <Label htmlFor="live_signing_secret">Live секрет подписи</Label>
                            <div className="flex items-center gap-2">
                                <div className="relative min-w-0 flex-1">
                                    <Input
                                        id="live_signing_secret"
                                        type={showSecret ? 'text' : 'password'}
                                        value={user.webhook_secret}
                                        readOnly
                                        className="font-mono text-sm pr-10"
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="absolute right-2 top-1/2 h-auto -translate-y-1/2 p-1"
                                        aria-label={showSecret ? 'Скрыть webhook-секрет' : 'Показать webhook-секрет'}
                                        onClick={() => setShowSecret(!showSecret)}
                                    >
                                        {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </Button>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    aria-label="Скопировать webhook-секрет"
                                    onClick={() => copyToClipboard(user.webhook_secret || '', 'webhook_secret')}
                                >
                                    {copiedField === 'webhook_secret'
                                        ? <Check className="h-4 w-4 text-green-600 dark:text-green-300" />
                                        : <Copy className="h-4 w-4" />}
                                </Button>
                            </div>
                            <p className="text-xs text-blue-700 dark:text-blue-300">
                                Используется для x-signature и проверки подписи callback в live-контуре.
                            </p>
                        </div>
                    )}
                </div>

                {isTypicalIntegration && user.sandbox_api_key && (
                    <div className="space-y-3 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950 p-4">
                        <div>
                            <p className="font-semibold text-emerald-900 dark:text-emerald-200">Sandbox</p>
                            <p className="text-xs text-emerald-700 dark:text-emerald-300">Тестовый контур без реальных операций</p>
                        </div>
                        <div className="space-y-2">
                            <Label>Sandbox API ключ</Label>
                            <div className="flex items-center gap-2">
                                <div className="relative min-w-0 flex-1">
                                    <Input
                                        type={showSandboxApiKey ? 'text' : 'password'}
                                        value={user.sandbox_api_key}
                                        readOnly
                                        className="font-mono text-sm pr-10"
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="absolute right-2 top-1/2 h-auto -translate-y-1/2 p-1"
                                        aria-label={showSandboxApiKey ? 'Скрыть Sandbox API-ключ' : 'Показать Sandbox API-ключ'}
                                        onClick={() => setShowSandboxApiKey(!showSandboxApiKey)}
                                    >
                                        {showSandboxApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </Button>
                                </div>
                                <Button variant="outline" size="sm" aria-label="Скопировать Sandbox API-ключ"
                                    onClick={() => copyToClipboard(user.sandbox_api_key || '', 'sandbox_api_key')}>
                                    {copiedField === 'sandbox_api_key' ? <Check className="h-4 w-4 text-green-600 dark:text-green-300" /> : <Copy className="h-4 w-4" />}
                                </Button>
                            </div>
                        </div>
                        {user.sandbox_webhook_secret && (
                            <div className="space-y-2">
                                <Label>Sandbox секрет подписи</Label>
                                <div className="flex items-center gap-2">
                                    <div className="relative min-w-0 flex-1">
                                        <Input
                                            type={showSandboxSecret ? 'text' : 'password'}
                                            value={user.sandbox_webhook_secret}
                                            readOnly
                                            className="font-mono text-sm pr-10"
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="absolute right-2 top-1/2 h-auto -translate-y-1/2 p-1"
                                            aria-label={showSandboxSecret ? 'Скрыть Sandbox webhook-секрет' : 'Показать Sandbox webhook-секрет'}
                                            onClick={() => setShowSandboxSecret(!showSandboxSecret)}
                                        >
                                            {showSandboxSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </Button>
                                    </div>
                                    <Button variant="outline" size="sm" aria-label="Скопировать Sandbox webhook-секрет"
                                        onClick={() => copyToClipboard(user.sandbox_webhook_secret || '', 'sandbox_webhook_secret')}>
                                        {copiedField === 'sandbox_webhook_secret' ? <Check className="h-4 w-4 text-green-600 dark:text-green-300" /> : <Copy className="h-4 w-4" />}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <details className="p-4 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <summary className="cursor-pointer text-sm font-semibold text-yellow-800 dark:text-yellow-200">Как использовать API · пример запроса</summary>
                    <div className="mt-3 flex items-start gap-2">
                        <Settings className="h-5 w-5 text-yellow-600 dark:text-yellow-300 mt-0.5" />
                        <div>
                            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                                Как использовать API
                            </p>
                            <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                                {isTypicalIntegration
                                    ? 'Подписывайте точные байты JSON-тела секретным ключом и передавайте подпись вместе с API-ключом.'
                                    : <>Добавьте заголовок <code className="bg-yellow-200 dark:bg-yellow-950 px-1 rounded">Authorization: Bearer &lt;API_KEY&gt;</code> к вашим API запросам.</>}
                            </p>
                            <div className="mt-3 p-2 bg-yellow-100 dark:bg-yellow-950 rounded text-xs">
                                <p className="font-medium text-yellow-800 dark:text-yellow-200 mb-1">Пример запроса:</p>
                                <pre className="text-yellow-700 dark:text-yellow-300 whitespace-pre-wrap">
                                    {isTypicalIntegration ? `POST /api/merchant/v2/deposits
Content-Type: application/json
x-api-key: <API_KEY>
x-signature: <lowercase HMAC-SHA256 от точных байтов тела>

{
  "order_id": "order-123",
  "amount": 1000,
  "currency": "${fiatCurrency}",
  "method": "${merchantMethods.length > 0 ? `<согласованный ${fiatCurrency}-метод>` : '<методы пока не настроены>'}",
  "callback_url": "https://merchant.example.com/callback",
  "success_url": "https://merchant.example.com/success",
  "pending_url": "https://merchant.example.com/pending",
  "fail_url": "https://merchant.example.com/fail"
}` : `POST /api/merchant/payments
Content-Type: application/json
Authorization: Bearer <API_KEY>

{
  "user_id": "user123",
  "amount": 1000,
  "merchantPaymentId": "pay_20260101_0001",
  "callback": "https://merchant.example.com/payments/webhook"
}`}
                                </pre>
                            </div>
                        </div>
                    </div>
                </details>

                <div className="pt-2">
                    <Button
                        variant="outline"
                        onClick={() => window.open(`${API_BASE_URL}/${isTypicalIntegration ? 'merchant-docs-v2' : 'merchant-docs'}`, '_blank', 'noopener,noreferrer')}
                    >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        {isTypicalIntegration ? 'Документация API v2.2' : 'Документация API'}
                    </Button>
                </div>

                {!isTypicalIntegration && <div className="pt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <Button
                        variant="secondary"
                        onClick={() => navigate(cabinetPath('merchant', '/api-test'), { state: { apiKey: user.api_key } })}
                    >
                        Протестировать платеж
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => navigate(cabinetPath('merchant', '/appeal-test'))}
                        className="border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-950 hover:text-orange-800 dark:hover:text-orange-200"
                    >
                        Протестировать апелляцию
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => navigate(cabinetPath('merchant', '/sandbox'))}
                        className="border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950 hover:text-emerald-800 dark:hover:text-emerald-200"
                    >
                        <TestTube className="h-4 w-4 mr-2" />
                        Тестовая среда
                    </Button>
                </div>}
            </CardContent>
        </Card>

        {/* Webhook Settings */}
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Webhook className="h-5 w-5 text-green-600 dark:text-green-300" />
                    Настройки Webhook
                </CardTitle>
                <CardDescription>
                    Настройте URL для получения уведомлений о статусе платежей
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {!isTypicalIntegration ? <div className="space-y-2">
                    <Label htmlFor="webhook_url">URL для уведомлений</Label>
                    <div className="flex gap-2">
                        <Input
                            id="webhook_url"
                            type="url"
                            value={webhookUrl}
                            onChange={(e) => setWebhookUrl(e.target.value)}
                            placeholder="https://your-site.com/webhook"
                            className="flex-1"
                        />
                        <Button
                            onClick={handleSaveWebhook}
                            disabled={webhookSaving || !webhookUrl.trim()}
                            className="bg-green-600 hover:bg-green-700"
                        >
                            {webhookSaving ? 'Сохранение...' : 'Сохранить'}
                        </Button>
                    </div>
                </div> : (
                    <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950 p-4 text-sm text-blue-800 dark:text-blue-200">
                        Callback URL передаётся обязательным полем <code>callback_url</code> в каждой операции.
                        Система отправляет callback только для финального SUCCESS или FAILED.
                    </div>
                )}

                {!isTypicalIntegration && user.webhook_secret && (
                    <div className="space-y-2">
                        <Label htmlFor="webhook_secret">Секретный ключ (Webhook Secret)</Label>
                        <div className="relative">
                            <Input
                                id="webhook_secret"
                                type={showSecret ? "text" : "password"}
                                value={user.webhook_secret}
                                readOnly
                                className="pr-20 font-mono text-sm"
                            />
                            <div className="absolute right-1 top-1 flex gap-1">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    aria-label={showSecret ? 'Скрыть webhook-секрет' : 'Показать webhook-секрет'}
                                    onClick={() => setShowSecret(!showSecret)}
                                >
                                    {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    aria-label="Скопировать webhook-секрет"
                                    onClick={() => copyToClipboard(user.webhook_secret || '', 'webhook_secret')}
                                >
                                    {copiedField === 'webhook_secret' ? (
                                        <Check className="h-4 w-4 text-green-600 dark:text-green-300" />
                                    ) : (
                                        <Copy className="h-4 w-4" />
                                    )}
                                </Button>
                            </div>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-slate-400">
                            {isTypicalIntegration
                                ? 'Используйте этот секрет для x-signature во входящих запросах и для проверки подписи callback.'
                                : 'Используйте этот ключ для проверки подписи уведомлений (X-Webhook-Signature)'}
                        </p>
                    </div>
                )}

                <div className="p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-start gap-2">
                        <Webhook className="h-5 w-5 text-blue-600 dark:text-blue-300 mt-0.5" />
                        <div>
                            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                                Формат уведомлений
                            </p>
                            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                                {isTypicalIntegration
                                    ? 'Финальный callback содержит только {"order_id":"..."}; актуальный статус запрашивается через GET /api/merchant/v2/transactions/{transaction_id}.'
                                    : 'Мы будем отправлять POST запросы с данными о статусе платежа в формате JSON. Включает: payment_id, status, amount, created_at, updated_at.'}
                            </p>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>

        {/* API IP allowlist */}
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                    <Network className="h-5 w-5 text-green-600 dark:text-green-300" />
                    Белый список IP
                </CardTitle>
                <CardDescription>
                    API-ключи можно использовать только с этих адресов. Допустимы IPv4,
                    IPv6 и CIDR-диапазоны. Пустой список — без ограничений.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="flex gap-2">
                    <Button
                        type="button"
                        size="sm"
                        variant={ipAllowlistEnvironment === 'live' ? 'default' : 'outline'}
                        onClick={() => setIPAllowlistEnvironment('live')}
                        disabled={ipAllowlistSaving}
                    >
                        Live
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        variant={ipAllowlistEnvironment === 'sandbox' ? 'default' : 'outline'}
                        onClick={() => setIPAllowlistEnvironment('sandbox')}
                        disabled={ipAllowlistSaving}
                    >
                        Sandbox
                    </Button>
                </div>
                {ipAllowlist.length === 0 ? (
                    <div className="flex min-h-28 items-center justify-center rounded-lg border border-dashed px-4 text-center text-sm text-gray-500 dark:text-slate-400">
                        Адресов нет — ключи можно использовать с любого IP
                    </div>
                ) : (
                    <div className="space-y-3">
                        {ipAllowlist.map((address, index) => (
                            <div key={index} className="flex items-start gap-2">
                                <div className="flex-1">
                                    <Input
                                        aria-label={`IP-адрес ${index + 1}`}
                                        value={address}
                                        placeholder="203.0.113.10 или 2001:db8::/32"
                                        className="font-mono"
                                        onChange={(event) => {
                                            const next = [...ipAllowlist];
                                            next[index] = event.target.value;
                                            setIPAllowlist(next);
                                            setIPAllowlistError(null);
                                            setIPAllowlistSuccess(null);
                                        }}
                                        disabled={ipAllowlistSaving}
                                    />
                                    {address.trim() && !isValidIPOrCIDR(address) && (
                                        <p className="mt-1 text-xs text-red-600 dark:text-red-300">
                                            Проверьте формат IP-адреса или CIDR
                                        </p>
                                    )}
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    aria-label={`Удалить IP-адрес ${index + 1}`}
                                    onClick={() => {
                                        setIPAllowlist((current) =>
                                            current.filter((_, itemIndex) => itemIndex !== index)
                                        );
                                        setIPAllowlistError(null);
                                        setIPAllowlistSuccess(null);
                                    }}
                                    disabled={ipAllowlistSaving}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}

                {ipAllowlistError && (
                    <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 p-3 text-sm text-red-600 dark:text-red-300">
                        {ipAllowlistError}
                    </div>
                )}
                {ipAllowlistSuccess && (
                    <div className="rounded-md border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950 p-3 text-sm text-green-700 dark:text-green-300">
                        {ipAllowlistSuccess}
                    </div>
                )}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                            if (ipAllowlist.length >= 50) {
                                setIPAllowlistError('Можно добавить не более 50 адресов');
                                return;
                            }
                            setIPAllowlist((current) => [...current, '']);
                            setIPAllowlistError(null);
                            setIPAllowlistSuccess(null);
                        }}
                        disabled={ipAllowlistSaving || ipAllowlist.length >= 50}
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        Добавить адрес
                    </Button>
                    <Button
                        type="button"
                        onClick={handleSaveIPAllowlist}
                        disabled={ipAllowlistSaving}
                        className="bg-slate-900 hover:bg-slate-800"
                    >
                        {ipAllowlistSaving ? 'Сохранение...' : 'Сохранить'}
                    </Button>
                </div>
            </CardContent>
        </Card>

    </div>;
};
